import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ComposeDialog } from "@/pages/comms/Communications";
import { TargetingDialog } from "@/components/comms/TargetingDialog";
import { TagReview } from "@/components/comms/TagReview";
import { useAuth } from "@/context/AuthContext";
import { Compass, Eye, Library, Mail, Search, Send, Tag, X, AlertTriangle, History, Tags } from "lucide-react";

const NON_RECRUITING = new Set(["System & Security", "Internal Recruiting Notifications"]);

function Chips({ opts, value, onChange, multi, testId }) {
  const sel = new Set(multi ? value || [] : value ? [value] : []);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {opts.map((o) => (
        <button key={o.key} type="button" onClick={() => {
          if (multi) { const n = new Set(sel); n.has(o.key) ? n.delete(o.key) : n.add(o.key); onChange([...n]); } else onChange(sel.has(o.key) ? null : o.key);
        }} aria-pressed={sel.has(o.key)} data-testid={`${testId}-${o.key}`}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${sel.has(o.key) ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600 hover:border-brand/50"}`}>{o.label}</button>
      ))}
    </div>
  );
}

function Step({ n, title, children, hint }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <p className="text-sm font-semibold text-slate-800 mb-2"><span className="text-brand mr-1.5">{n}.</span>{title}{hint && <span className="ml-2 text-xs font-normal text-slate-400">{hint}</span>}</p>
      {children}
    </div>
  );
}

function Recipients({ list, setList }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/persons", { params: { q, page_size: 8 } }).then((r) => setRes(r.data.items || r.data.persons || [])).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const add = async (p) => {
    setQ(""); setRes([]);
    if (list.some((x) => x.person_id === p.id)) return;
    try { const { data } = await api.get(`/email-center/person-context/${p.id}`); setList((l) => [...l, data]); }
    catch (e) { toast.error(apiError(e)); }
  };
  const eligible = list.filter((r) => r.eligible);
  const mixed = new Set(eligible.map((r) => `${r.program || "?"}|${r.stage || "?"}`)).size > 1;
  return (
    <div data-testid="ec-recipients">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people by name or email" className="pl-8" data-testid="ec-recipient-search" />
        {res.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-64 overflow-auto">
            {res.map((p) => (
              <button key={p.id} onClick={() => add(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50" data-testid={`ec-recipient-opt-${p.id}`}>
                <span className="text-sm text-slate-800">{p.first_name} {p.last_name}</span>
                <span className="block text-[11px] text-slate-400">{p.email || "No email"} · {p.organization_name || ""}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {list.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {list.map((r) => (
            <li key={r.person_id} className={`flex items-start justify-between gap-2 rounded-md border px-3 py-2 ${r.eligible ? "border-slate-200" : "border-rose-200 bg-rose-50"}`} data-testid={`ec-recipient-${r.person_id}`}>
              <div className="min-w-0 text-sm">
                <p className="font-medium text-slate-800">{r.name} <span className="text-xs font-normal text-slate-400">{r.email || ""}</span></p>
                <p className="text-[11px] text-slate-500">{[r.stage && `Stage: ${r.stage}`, r.recruiter_name && `Recruiter: ${r.recruiter_name}`, `${r.emails_sent} email(s) sent`, `${r.logged_contacts} logged contact(s)`].filter(Boolean).join(" · ")}</p>
                {!r.eligible && <p className="text-[11px] text-rose-700 font-medium" data-testid={`ec-excluded-${r.person_id}`}>Excluded: {r.excluded_reason}</p>}
              </div>
              <button onClick={() => setList((l) => l.filter((x) => x.person_id !== r.person_id))} aria-label="Remove recipient" className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      {mixed && <p className="mt-2 text-xs text-amber-700 flex items-center gap-1" data-testid="ec-mixed-warning"><AlertTriangle className="w-3.5 h-3.5" />These people are at different programs or stages. Consider sending to them in separate groups so each gets a fitting message.</p>}
    </div>
  );
}

function TemplateCard({ t, onPreview, onUse }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col" data-testid={`ec-rec-${t.id}`}>
      <p className="text-sm font-semibold text-slate-900">{t.name}</p>
      {t.description && <p className="text-xs text-slate-600 mt-0.5">{t.description}</p>}
      <p className="text-[11px] text-slate-400 mt-1">{[t.audience && `For: ${t.audience}`, t.use_case && `Use when: ${t.use_case}`, t.tone && `Tone: ${t.tone}`].filter(Boolean).join(" · ") || t.category}</p>
      <p className="text-xs text-slate-700 mt-2 truncate"><span className="text-slate-400">Subject:</span> {t.subject || "—"}</p>
      <p className="text-[11px] text-emerald-700 mt-1">{t.reasons.join(" · ")}</p>
      {t.misses?.length > 0 && <p className="text-[11px] text-amber-700">{t.misses.join(" · ")}</p>}
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => onPreview(t)} data-testid={`ec-preview-${t.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Preview</Button>
        <Button size="sm" onClick={() => onUse(t)} className="bg-brand hover:bg-brand-strong" data-testid={`ec-use-${t.id}`}><Send className="w-3.5 h-3.5 mr-1" />Use This Email</Button>
      </div>
    </div>
  );
}

function Finder({ tax, recipients, setRecipients, onPreview, onUse }) {
  const [b, setB] = useState({ purpose_group: null, purpose: null, relationship: null, backgrounds: [], familiarity: null, program: null });
  const [rec, setRec] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const first = recipients.find((r) => r.eligible);
  useEffect(() => {
    if (!first?.suggested) { setSuggested(null); return; }
    setSuggested(first.suggested);
    setB((s) => ({ ...s, purpose_group: s.purpose_group || first.suggested.purpose_group || null, program: s.program || first.suggested.program || null,
      relationship: s.relationship || first.suggested.relationship || null }));
  }, [first?.person_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.post("/email-center/recommend", { ...b, stage: first?.stage || null }).then((r) => setRec(r.data)).catch(() => setRec(null));
  }, [b, first?.stage]);
  const set = (k) => (v) => setB((s) => ({ ...s, [k]: v, ...(k === "purpose_group" ? { purpose: null } : {}) }));
  const group = tax.purposes.find((g) => g.key === b.purpose_group);
  const list = showAll ? rec?.all || [] : rec?.top || [];
  return (
    <div className="grid xl:grid-cols-5 gap-4 [&>*]:min-w-0">
      <div className="xl:col-span-2 rounded-xl border border-slate-200 bg-white p-4" data-testid="ec-finder">
        <Step n="1" title="Who is this for?" hint="optional — you can also pick them later"><Recipients list={recipients} setList={setRecipients} /></Step>
        {suggested?.why && <p className="text-[11px] text-sky-700 bg-sky-50 rounded px-2 py-1 my-2" data-testid="ec-suggested">Suggested from their record (not verified): {suggested.why}. Change anything below.</p>}
        <Step n="2" title="What do you want to accomplish?">
          <Chips opts={tax.purposes} value={b.purpose_group} onChange={set("purpose_group")} testId="ec-purpose-group" />
          {group && <div className="mt-2 pl-2 border-l-2 border-brand/30"><Chips opts={group.items} value={b.purpose} onChange={set("purpose")} testId="ec-purpose" /></div>}
        </Step>
        <Step n="3" title="What is the existing relationship?"><Chips opts={tax.relationships} value={b.relationship} onChange={set("relationship")} testId="ec-relationship" /></Step>
        <Step n="4" title="Professional background" hint="choose any"><Chips multi opts={tax.backgrounds} value={b.backgrounds} onChange={set("backgrounds")} testId="ec-background" /></Step>
        <Step n="5" title="Familiarity with us"><Chips opts={tax.familiarity} value={b.familiarity} onChange={set("familiarity")} testId="ec-familiarity" /></Step>
        <Step n="6" title="Which program or opportunity?"><Chips opts={tax.programs} value={b.program} onChange={set("program")} testId="ec-program" /></Step>
      </div>
      <div className="xl:col-span-3 space-y-3" data-testid="ec-recommendations">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">{showAll ? `All matching templates (${rec?.all?.length || 0})` : "Best matches"}</p>
          {(rec?.all?.length || 0) > 3 && <button onClick={() => setShowAll((v) => !v)} className="text-xs font-medium text-brand hover:underline" data-testid="ec-see-all">{showAll ? "Show best 3" : `See all ${rec.all.length} matching templates`}</button>}
        </div>
        {rec?.note && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="ec-note">{rec.note}</p>}
        {!b.purpose_group && <p className="text-sm text-slate-400">Pick what you want to accomplish to see recommendations.</p>}
        {b.purpose_group && rec && !rec.all.length && <p className="text-sm text-slate-500" data-testid="ec-no-match">No approved templates match yet. Try Browse Templates.</p>}
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{b.purpose_group && list.map((t) => <TemplateCard key={t.id} t={t} onPreview={onPreview} onUse={onUse} />)}</div>
      </div>
    </div>
  );
}

function Browse({ tax, onPreview, onUse }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/comm-templates", { params: { status: "active" } }).then((r) => setItems(r.data.items.filter((t) => !NON_RECRUITING.has(t.category) && t.type !== "system" && !t.customizes_system_key))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const groups = useMemo(() => {
    const g = {};
    (items || []).filter((t) => `${t.name} ${t.description || ""} ${t.subject || ""}`.toLowerCase().includes(q.toLowerCase()))
      .forEach((t) => { (g[t.category || "General"] = g[t.category || "General"] || []).push(t); });
    return Object.entries(g);
  }, [items, q]);
  if (items === null) return <p className="text-sm text-slate-400">Loading…</p>;
  return (
    <div data-testid="ec-browse">
      <div className="relative w-full sm:w-80 mb-4"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, description or subject" className="pl-8" data-testid="ec-browse-search" /></div>
      <div className="space-y-3">
        {groups.map(([cat, ts]) => (
          <details key={cat} open={q ? true : undefined} className="rounded-lg border border-slate-200 bg-white" data-testid={`ec-cat-${cat}`}>
            <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-800">{cat} <span className="text-slate-400 font-normal">({ts.length})</span></summary>
            <ul className="divide-y divide-slate-100 border-t border-slate-100">
              {ts.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2" data-testid={`ec-tpl-${t.id}`}>
                  <div className="min-w-0"><p className="text-sm text-slate-800">{t.name}{t.targeting?.purposes?.length ? <span className="ml-2 text-[10px] rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-0.5" data-testid={`ec-tagged-${t.id}`}>Tagged</span> : null}</p><p className="text-[11px] text-slate-400 truncate">{t.description || t.subject}</p></div>
                  <div className="flex gap-1.5">
                    {t.can_edit && <Button size="sm" variant="ghost" onClick={() => setEdit(t)} data-testid={`ec-tag-${t.id}`}><Tag className="w-3.5 h-3.5 mr-1" />Tags</Button>}
                    <Button size="sm" variant="outline" onClick={() => onPreview(t)} data-testid={`ec-browse-preview-${t.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" onClick={() => onUse(t)} className="bg-brand hover:bg-brand-strong" data-testid={`ec-browse-use-${t.id}`}>Use</Button>
                  </div>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
      {edit && <TargetingDialog template={edit} taxonomy={tax} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function Recent() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/communications", { params: { sender: "me" } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (items === null) return <p className="text-sm text-slate-400">Loading…</p>;
  if (!items.length) return <p className="text-sm text-slate-400" data-testid="ec-recent-empty">You haven't sent any emails from Top-Recruit yet.</p>;
  return (
    <ul className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="ec-recent">
      {items.slice(0, 100).map((c) => (
        <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
          <span className="min-w-0"><span className="text-slate-800">{c.subject || "(no subject)"}</span><span className="block text-[11px] text-slate-400">To {c.person_name || c.to_name || c.to_email} · {new Date(c.sent_at || c.created_at).toLocaleString()}</span></span>
          <span className={`text-[11px] rounded-full px-2 py-0.5 ${c.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} title={c.error || ""}>{c.status === "sent" ? "Sent (submitted)" : c.status}</span>
        </li>
      ))}
    </ul>
  );
}

export default function EmailCenter() {
  const { isManager, isAdmin, isGroupAdmin, isSuper } = useAuth();
  const canReview = isManager || isAdmin || isGroupAdmin || isSuper;
  const [mode, setMode] = useState("find");
  const [tax, setTax] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [preview, setPreview] = useState(null);
  const [queue, setQueue] = useState(null);
  useEffect(() => { api.get("/email-center/taxonomy").then((r) => setTax(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const use = (t) => {
    const ok = recipients.filter((r) => r.eligible);
    if (recipients.length && !ok.length) { toast.error("Every selected recipient is excluded (Do Not Contact or no valid email). Remove them or choose someone else."); return; }
    setQueue({ template: t, people: ok.length ? ok : [null], i: 0 });
  };
  const openPreview = async (t) => {
    try { const { data } = await api.get("/comm-templates", { params: { status: "active" } }); setPreview(data.items.find((x) => x.id === t.id) || t); }
    catch { setPreview(t); }
  };
  if (!tax) return <p className="text-sm text-slate-400">Loading…</p>;
  const cur = queue && queue.people[queue.i];
  return (
    <div data-testid="email-center">
      <div className="flex flex-wrap rounded-lg border border-slate-200 bg-white p-0.5 mb-4 max-w-full" role="tablist">
        {[["find", "Find an Email", Compass], ["browse", "Browse Templates", Library], ["recent", "Recent Emails", History], ...(canReview ? [["review", "Tag Templates", Tags]] : [])].map(([k, l, I]) => (
          <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)} data-testid={`ec-mode-${k}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === k ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"}`}><I className="w-4 h-4" />{l}</button>
        ))}
      </div>
      {mode === "find" && <Finder tax={tax} recipients={recipients} setRecipients={setRecipients} onPreview={openPreview} onUse={use} />}
      {mode === "browse" && <Browse tax={tax} onPreview={openPreview} onUse={use} />}
      {mode === "recent" && <Recent />}
      {mode === "review" && canReview && <TagReview tax={tax} canCreate={canReview} />}
      {preview && (
        <Dialog open onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="bg-white max-w-2xl" data-testid="ec-preview-dialog" aria-describedby={undefined}>
            <DialogHeader><DialogTitle>{preview.name}</DialogTitle></DialogHeader>
            <p className="text-sm"><span className="text-slate-400">Subject:</span> {preview.subject}</p>
            <div className="prose prose-sm max-w-none border border-slate-100 rounded-md p-3 max-h-[55vh] overflow-auto" dangerouslySetInnerHTML={{ __html: preview.bodyhtml || "" }} />
            <p className="text-[11px] text-slate-400">Merge fields like {"{{FirstName}}"} are filled in for each recipient in the composer.</p>
          </DialogContent>
        </Dialog>
      )}
      {queue && (
        <ComposeDialog key={`${queue.template.id}-${queue.i}`} personId={cur?.person_id} initialTemplateId={queue.template.id}
          title={queue.people.length > 1 ? `Compose Email · ${queue.i + 1} of ${queue.people.length}: ${cur?.name}` : undefined}
          onSent={() => setRecipients((l) => l.map((r) => (r.person_id === cur?.person_id ? { ...r, emails_sent: r.emails_sent + 1 } : r)))}
          onClose={() => setQueue((q) => (q && q.i + 1 < q.people.length ? { ...q, i: q.i + 1 } : null))} />
      )}
      <p className="text-[11px] text-slate-400 mt-6 flex items-center gap-1"><Mail className="w-3 h-3" />Each email is reviewed and sent one at a time from your connected mailbox, then logged on the person's record.</p>
    </div>
  );
}
